import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { combineDateTime, validateSchedule } from "@/lib/wib";

// Satu draft jadwal (WIB) untuk seluruh alur peminjam: katalog → detail →
// checkout. Disimpan di localStorage di bawah key khusus prototipe M2 —
// tidak menyentuh `gudang_cart_v2` milik keranjang. Edit selalu lewat draft
// lokal dialog; hanya Apply yang persist.
const KEY = "dexter.booking.v2";

const newKey = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const EMPTY = { pickup_date: "", return_date: "", pickup_time: "09:00", return_time: "17:00", location_source: "", location_id: "", custom_location: "", booking_type: "", purpose: "", owner_id: "", idempotency_key: "" };

function read() {
  try {
    const current = localStorage.getItem(KEY);
    const raw = current || localStorage.getItem("dexter.schedule.v1");
    if (!raw) return { ...EMPTY };
    const p = JSON.parse(raw);
    return {
      ...EMPTY, ...p,
      legacy_schedule: !current,
      pickup_date: typeof p.pickup_date === "string" ? p.pickup_date : "",
      return_date: typeof p.return_date === "string" ? p.return_date : "",
      pickup_time: /^\d{2}:\d{2}$/.test(p.pickup_time || "") ? p.pickup_time : "09:00",
      return_time: /^\d{2}:\d{2}$/.test(p.return_time || "") ? p.return_time : "17:00",
    };
  } catch {
    return { ...EMPTY };
  }
}

const ScheduleContext = createContext(null);

export function ScheduleProvider({ children }) {
  const [draft, setDraft] = useState(read);
  const { user } = useAuth();
  const { clear: clearCart } = useCart();
  useEffect(() => {
    if (user === null) return;
    const owner = user?.id || "";
    const previous = localStorage.getItem("dexter.draft.owner");
    if ((draft.owner_id && draft.owner_id !== owner) || (previous && previous !== owner) || user === false) {
      const empty = { ...EMPTY, owner_id: owner };
      setDraft(empty); localStorage.setItem(KEY, JSON.stringify(empty)); clearCart();
    }
    localStorage.setItem("dexter.draft.owner", owner);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback((next) => {
    next = { ...next, owner_id: user?.id || "", idempotency_key: newKey() };
    setDraft(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Storage penuh/diblokir: state sesi tetap jalan, tidak crash.
    }
  }, [user]);

  const clear = useCallback(() => {
    persist({ ...EMPTY });
  }, [persist]);

  const checked = useMemo(() => {
    const pickup = combineDateTime(draft.pickup_date, draft.pickup_time);
    const ret = combineDateTime(draft.return_date, draft.return_time);
    const v = validateSchedule(pickup, ret);
    return { pickup, ret, ...v };
  }, [draft]);

  const value = useMemo(() => ({
    draft,
    ready: checked.ok && ["WITH_EQUIPMENT", "LOCATION_ONLY"].includes(draft.booking_type) && (draft.location_source === "MASTER" ? !!draft.location_id : draft.location_source === "CUSTOM" && !!draft.custom_location.trim()),
    payload: { idempotency_key: draft.idempotency_key, start_time: checked.pickup ? `${checked.pickup}:00+07:00` : null, end_time: checked.ret ? `${checked.ret}:00+07:00` : null, booking_type: draft.booking_type, location_source: draft.location_source, location_id: draft.location_source === "MASTER" ? draft.location_id : null, custom_location: draft.location_source === "CUSTOM" ? draft.custom_location.trim() : null },
    patch: (p) => persist({ ...draft, ...p }),
    clear,
    ...checked,
  }), [draft, persist, clear, checked]);

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>;
}

export const useSchedule = () => useContext(ScheduleContext);
